import * as faceapi from '@vladmandic/face-api';

let modelsLoaded = false;

/**
 * Carrega os modelos da face-api.js a partir da pasta /public/models
 */
export async function loadFaceApiModels(): Promise<void> {
  if (modelsLoaded) return;
  
  const MODEL_URL = '/models';
  
  try {
    console.log('Carregando modelos face-api...');
    await Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
      faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
    ]);
    modelsLoaded = true;
    console.log('Modelos face-api carregados com sucesso!');
  } catch (error) {
    console.error('Erro ao carregar modelos face-api:', error);
    throw error;
  }
}

/**
 * Calcula a distância euclidiana entre dois pontos bidimensionais
 */
function dist(p1: { x: number; y: number }, p2: { x: number; y: number }): number {
  return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
}

/**
 * Calcula o EAR (Eye Aspect Ratio) para um olho específico
 */
export function calculateEyeEAR(eyePoints: { x: number; y: number }[]): number {
  if (!eyePoints || eyePoints.length < 6) return 0;
  
  const p1 = eyePoints[0];
  const p2 = eyePoints[1];
  const p3 = eyePoints[2];
  const p4 = eyePoints[3];
  const p5 = eyePoints[4];
  const p6 = eyePoints[5];

  const vertical1 = dist(p2, p6);
  const vertical2 = dist(p3, p5);
  const horizontal = dist(p1, p4);

  return (vertical1 + vertical2) / (2.0 * horizontal);
}

/**
 * Calcula o EAR médio para os dois olhos
 */
export function calculateEAR(landmarks: any): number {
  const leftEye = landmarks.getLeftEye();
  const rightEye = landmarks.getRightEye();
  
  const leftEAR = calculateEyeEAR(leftEye);
  const rightEAR = calculateEyeEAR(rightEye);
  
  return (leftEAR + rightEAR) / 2;
}

/**
 * Obtém o descritor facial (embedding) a partir de uma imagem/canvas/vídeo
 */
export async function getFaceDescriptor(
  input: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement
): Promise<Float32Array | null> {
  await loadFaceApiModels();
  
  const detection = await faceapi
    .detectSingleFace(input, new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 }))
    .withFaceLandmarks()
    .withFaceDescriptor();
    
  return detection ? detection.descriptor : null;
}

/**
 * Obtém o descritor facial a partir de uma URL de imagem externa
 */
export async function getFaceDescriptorFromUrl(url: string): Promise<Float32Array | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = url;
    
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = (e) => reject(new Error('Erro ao carregar a imagem da URL: ' + url));
    });
    
    return await getFaceDescriptor(img);
  } catch (error) {
    console.error('Erro ao processar imagem de referência por URL:', error);
    return null;
  }
}

/**
 * Compara dois descritores faciais e retorna a similaridade (0 a 100) e se é compatível
 * @param descriptor1 Primeiro descritor facial
 * @param descriptor2 Segundo descritor facial
 * @param threshold Limiar de distância euclidiana (menor que este valor é considerado a mesma pessoa. Padrão: 0.6)
 */
export function compareFaces(
  descriptor1: Float32Array,
  descriptor2: Float32Array,
  threshold = 0.55
): { isMatch: boolean; confidence: number; distance: number } {
  const distance = faceapi.euclideanDistance(descriptor1, descriptor2);
  
  // A distância varia de 0 (idêntico) a ~1.5 (totalmente diferente).
  // Mapeamos para uma confiança percentual simples
  const confidence = Math.round(Math.max(0, 1 - (distance / threshold)) * 100);
  const isMatch = distance < threshold;
  
  return {
    isMatch,
    confidence: isMatch ? Math.min(100, 50 + confidence / 2) : Math.max(0, confidence),
    distance
  };
}
